import React, {useContext} from 'react';
import getConfig from 'next/config';

import TeaserContent from '~/src/components/teaser/content/TeaserContent';
import TeaserContentPlaceholder from '~/src/components/teaser/content/placeholder/TeaserContentPlaceholder';
import EmbedIframe from '~/src/components/embed/iframe/EmbedIframe';
import TeaserAd from '~/src/components/teaser/ad/TeaserAd';
import DebugTeaser from '~/src/components/debug/teaser/DebugTeaser';
import TeaserSection from '~/src/components/teaser/section/TeaserSection';
import TeaserTopPopular from '~/src/components/teaser/top/popular/TeaserTopPopular';
import TeaserTopCommented from './top/commented/TeaserTopCommented';
import TeaserRecentlyCommented from './recently/TeaserRecentlyCommented';
import LifestyleQuoteTeaser from '~/src/components/lifestyle/quoteTeaser/LifestyleQuoteTeaser';
import EmbedBlocker from '~/src/components/embed/blocker/Blocker';
import TeaserTopItem from '~/src/components/teaser/top/item/TeaserTopItem';

import {classNames, getFormatClassNames} from '~/src/utilities/helpers/css';
import {TeaserFormat, AdFormat} from '~/src/utilities/constants/formats';
import {TeaserTypes} from '~/src/utilities/constants/types';
import {isEnabled} from '~/src/utilities/helpers/feature';
import {Feature} from '~/src/utilities/constants/feature';
import {SettingsContext} from '~/src/components/context/SettingsContext';
import {ConsentContext} from '~/src/components/context/ConsentContext';
import {withErrorBoundary} from '~/src/components/errorBoundary/ErrorBoundary';
import {SectionLayout} from '~/src/utilities/constants/layout';
import rollbar from '~/src/utilities/libs/rollbar';

import css from './Teaser.module.scss';

const {publicRuntimeConfig: config} = getConfig();

const Teaser = (props) => {
    const {selected} = props;
    const settings = useContext(SettingsContext);
    const consent = useContext(ConsentContext);

    const className = classNames(css, 'teaser', props.type, props.layout, settings.siteArea, getFormatClassNames(props.format), {
        selected
    });

    switch (props.type) {
        case TeaserTypes.AD:
            if (props.format.primaryGrid === AdFormat.INSIDE_QUARTER_POS) {
                return (
                    <article className={className} data-testid="TeaserAd">
                        <TeaserAd {...props} />
                    </article>
                );
            }
            return <TeaserAd className={className} {...props} />;
        case TeaserTypes.IFRAME:
            return (
                <article className={className}>
                    {consent.allowsAll ? <EmbedIframe className={className} {...props} url={props.src} /> : <EmbedBlocker />}
                </article>
            );

        case TeaserTypes.MOST_POPULAR:
            return (
                <article className={className}>
                    <TeaserTopPopular {...props} />
                </article>
            );
        case TeaserTypes.POPULARITY:
            return <TeaserTopItem {...props} className={css.topItem} />;
        case TeaserTypes.WIDGET_MOST_COMMENTED:
            return <TeaserTopCommented className={className} {...props} />;
        case TeaserTypes.WIDGET_RECENTLY_COMMENTED:
            return <TeaserRecentlyCommented className={className} {...props} />;
        case TeaserTypes.ARTICLES:
        case TeaserTypes.BREAKINGNEWS:
        case TeaserTypes.TICKERS:
        case TeaserTypes.SLIDESHOWS:
        case TeaserTypes.LINKS:
        case TeaserTypes.VIDEOS:
        case TeaserTypes.EMBEDS:
            if (props.type === TeaserTypes.VIDEOS && !isEnabled(Feature.VIDEO)) {
                return null;
            }
            return (
                <article className={className} data-testid={`Teaser${props.format.primaryGrid === TeaserFormat.TEASER_LARGE ? 'Large' : 'Small'}`}>
                    <TeaserContent {...props} />
                </article>
            );
        case TeaserTypes.PLACEHOLDER:
            return (
                <article className={className}>
                    <TeaserContentPlaceholder {...props} />
                </article>
            );
        case TeaserTypes.SLIDER:
            if (isEnabled(Feature.DEEPLINK_SLIDER)) {
                return <TeaserSection {...props.data} options={props.options} layout={SectionLayout.CAROUSEL} carouselType="categorySlider" />;
            }
            return null;
        case TeaserTypes.LIFESTYLE_QUOTE:
            return (
                <article className={className}>
                    <LifestyleQuoteTeaser {...props} />
                </article>
            );
        default:
            rollbar.warning(`[TeaserElement] Type is not supported: ${props.type}`, props);

            return (
                config.debug.showComponents && (
                    <article className={className}>
                        <DebugTeaser {...props} />
                    </article>
                )
            );
    }
};

export default withErrorBoundary(Teaser, 'Teaser');
